import { ComponentType, ReactElement, ReactNode } from 'react';

import styled from 'styled-components';

import { IconButton } from '@components/icon-button';
import { IconProps } from '@components/icons';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconRemove } from '@components/icons/remove';
import { Menu } from '@components/menu';
import { MenuItemProps } from '@components/menu/menu';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { pxToRem } from '@utils/converters';

import { useFormBuilderContext } from './context';
import { DragHandleProps, SmallDragHandle } from './drag-handle';
import { StateField } from './state';

export type FieldEditHeaderProps = {
  content: ReactElement;
  menu?: Array<MenuItemProps>;
  dragHandle?: ReactElement<DragHandleProps>;
  field: StateField;
};

export const FieldEditHeaderActions = ({
  menu,
  field,
}: Pick<FieldEditHeaderProps, 'menu' | 'field'>) => {
  const {
    store: { useIsSaving, actions },
  } = useFormBuilderContext();
  const isformSaving = useIsSaving();

  return (
    <ActionsContainer>
      {field.builderProps?.canDuplicate !== false && (
        <IconButton
          dataTestid={`formbuilder-${field.kind}-duplicate`}
          disabled={isformSaving}
          variant="ghost-purple"
          icon={<IconDuplicate />}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            actions.duplicateField(field.id);
          }}
        />
      )}

      {field.builderProps?.canRemove !== false && (
        <IconButton
          dataTestid={`formbuilder-${field.kind}-delete`}
          variant="ghost-purple"
          icon={<IconRemove />}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            actions.removeField(field.id);
          }}
        />
      )}

      {menu && menu.length > 0 && (
        <Menu
          dataTestid={`formbuilder-${field.kind}-actions`}
          width="auto"
          buttonProps={{
            variant: 'ghost',
            iconRight: <IconMoreVertical />,
          }}
        >
          {menu.map((item) => (
            <Menu.Item key={item.id} {...item} />
          ))}
        </Menu>
      )}
    </ActionsContainer>
  );
};

export const FieldEditHeader = ({ dragHandle, menu, content, field }: FieldEditHeaderProps) => {
  return (
    <Container>
      <ContentContainer data-testid="formbuilder-edit-field-container">
        {dragHandle}
        {content}
      </ContentContainer>
      <FieldEditHeaderActions menu={menu} field={field} />
    </Container>
  );
};

FieldEditHeader.DragHandle = SmallDragHandle;

type HeaderTitleProps = {
  icon?: ComponentType<IconProps>;
  label: string;
  dragHandle?: ReactNode;
};

const HeaderTitle = ({ icon: HeaderIcon, label, dragHandle }: HeaderTitleProps) => {
  return (
    <>
      {dragHandle}
      {HeaderIcon && <HeaderIcon color="elementPurple50" />}
      <Text font="regular14" text={label} ellipsis />
    </>
  );
};
HeaderTitle.displayName = 'FieldEditHeader.Title';
FieldEditHeader.Title = HeaderTitle;

const ActionsContainer = styled(Stack).attrs({
  space: 0,
  orientation: 'horizontal',
  align: 'center',
  'data-c': 'edit-header-actions',
})({
  minHeight: pxToRem(40),
  zIndex: 2,
});

const Container = styled(Stack).attrs({
  orientation: 'horizontal',
  align: 'center',
  space: 2,
})({
  width: '100%',
});

const ContentContainer = styled(Stack).attrs({
  space: 2,
  orientation: 'horizontal',
  align: 'center',
})({
  flexGrow: 1,
  overflow: 'hidden',
});
